import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Star, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Bottom-sheet modal to submit a review for a partner (broker/advocate/architect/…).
 *  Props:
 *   - open (bool)
 *   - onClose ()
 *   - targetId (string)   — partner user id
 *   - targetType (string) — partner role, must match backend Literal
 *   - targetName (string) — display only
 *   - onSubmitted (review)? — called after successful POST /reviews
 */
export default function WriteReviewSheet({ open, onClose, targetId, targetType, targetName, onSubmitted }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Reset form each time the sheet opens.
    useEffect(() => {
        if (open) { setRating(0); setHover(0); setComment(""); }
    }, [open]);

    if (!open) return null;

    const submit = async () => {
        if (!user) {
            onClose();
            navigate(`/login?next=/partners/${targetId}`);
            return;
        }
        if (!rating) return toast.error("Please pick a rating");
        setSubmitting(true);
        try {
            const { data } = await api.post("/reviews", {
                target_type: targetType,
                target_id: targetId,
                rating,
                comment: comment.trim() || null,
            });
            toast.success("Review submitted — thank you!");
            onSubmitted?.(data);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit review");
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" data-testid="write-review-sheet">
            {/* Backdrop */}
            <div
                className="absolute inset-0 animate-fade-in"
                style={{ background: "rgba(0, 15, 35, 0.65)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
                onClick={onClose}
            />

            {/* Sheet */}
            <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-in-right sm:animate-fade-in" style={{ maxHeight: "85vh" }}>
                {/* Handle bar (mobile only) */}
                <div className="sm:hidden pt-2 flex justify-center">
                    <div className="w-10 h-1 rounded-full" style={{ background: "rgba(0,38,87,0.2)" }} />
                </div>

                {/* Header */}
                <div className="px-5 pt-3 pb-3 flex items-start justify-between gap-3 border-b" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>Write a Review</div>
                        <div className="font-heading font-extrabold text-[15px] truncate mt-0.5" style={{ color: "#002657" }}>
                            {targetName || "Partner"}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="wr-close"
                        className="w-9 h-9 rounded-full flex items-center justify-center bg-[#f6f7fb] hover:bg-[#e8ebf3] active:scale-95 transition-transform"
                    >
                        <X className="w-4 h-4" style={{ color: "#002657" }} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 pt-4 pb-3 overflow-y-auto" style={{ maxHeight: "60vh" }}>
                    {/* Star picker */}
                    <div className="text-center">
                        <div className="text-[11px] uppercase tracking-widest font-extrabold" style={{ color: "#002657" }}>Your Rating</div>
                        <div
                            className="mt-2 flex items-center justify-center gap-1"
                            onMouseLeave={() => setHover(0)}
                            data-testid="wr-stars"
                        >
                            {[1, 2, 3, 4, 5].map((n) => {
                                const active = (hover || rating) >= n;
                                return (
                                    <button
                                        type="button"
                                        key={n}
                                        onClick={() => setRating(n)}
                                        onMouseEnter={() => setHover(n)}
                                        aria-label={`${n} star`}
                                        data-testid={`wr-star-${n}`}
                                        className="p-1 active:scale-90 transition-transform"
                                    >
                                        <Star
                                            className="w-8 h-8"
                                            style={{
                                                color: active ? "#DAA520" : "rgba(0,38,87,0.2)",
                                                fill:  active ? "#DAA520" : "transparent",
                                            }}
                                            strokeWidth={1.8}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-1 text-[11px]" style={{ color: rating ? "#8B6712" : "rgba(0,38,87,0.5)" }}>
                            {rating ? RATING_LABELS[rating] : "Tap to rate"}
                        </div>
                    </div>

                    {/* Comment */}
                    <div className="mt-4">
                        <label className="text-[11px] uppercase tracking-widest font-extrabold" style={{ color: "#002657" }}>
                            Comment <span className="normal-case font-medium" style={{ color: "rgba(0,38,87,0.5)" }}>(optional)</span>
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value.slice(0, 500))}
                            placeholder="Share your experience — what did they do well?"
                            data-testid="wr-comment"
                            rows={4}
                            className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-[#f6f7fb] border text-[13px] leading-relaxed focus:outline-none focus:border-[#DAA520]"
                            style={{ color: "#002657", borderColor: "rgba(0,38,87,0.15)" }}
                        />
                        <div className="mt-1 text-right text-[10px]" style={{ color: "rgba(0,38,87,0.45)" }}>
                            {comment.length}/500
                        </div>
                    </div>

                    {/* Non-user hint */}
                    {!user && (
                        <div className="mt-2 rounded-xl px-3 py-2 text-[11px] flex items-start gap-2" style={{ background: "#FBF6E0", color: "#8B6712" }}>
                            <MessageCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            You&apos;ll be asked to log in before your review is posted.
                        </div>
                    )}
                </div>

                {/* Footer action */}
                <div className="px-5 pt-2 pb-4 border-t" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        data-testid="wr-submit"
                        className="w-full py-3 rounded-2xl font-extrabold text-[14px] shadow disabled:opacity-60 active:scale-[0.99] transition-transform"
                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                    >
                        {submitting
                            ? <span className="inline-flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Posting…</span>
                            : "Submit Review"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

const RATING_LABELS = {
    1: "Poor",
    2: "Below Average",
    3: "Good",
    4: "Very Good",
    5: "Excellent",
};
